import { useMemo, useEffect, useState } from 'react';
import { useNodesState, useEdgesState } from '@xyflow/react';
import type {
    AIFlow,
    FlowNode,
    FlowEdge,
    FlowOrderItem,
    NodeEdgesContextProviderProps,
    ContextVariable,
} from '@app/services/aiFlow.types';
import { NodesEdgesContext } from './useNodesEdges';
import { isNodeComplete } from '../utils/validate-flow';
import { parseSchema } from '../utils/import-json-flow';

const NodesEdgesProvider = ({
    children,
    initialData,
}: NodeEdgesContextProviderProps & { initialData: AIFlow }) => {
    const [nodes, setNodes, onNodesChange] = useNodesState<FlowNode>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<FlowEdge>([]);
    const [contextVariables, setContextVariables] = useState<ContextVariable[]>([]);

    useEffect(() => {
        if (initialData?.schema) {
            const { nodes, edges, context } = parseSchema(initialData.schema);
            setNodes(nodes);
            setEdges(edges);
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setContextVariables(context);
        }
    }, [initialData, setNodes, setEdges, setContextVariables]);

    const [flowOrder, totalOrderedFlows] = useMemo(() => orderNodes(nodes, edges), [edges, nodes]);

    const incompleteNodeIds = useMemo(
        () =>
            nodes.reduce<string[]>((acc, n) => {
                if (!isNodeComplete(n)) {
                    acc.push(n.id);
                }
                return acc;
            }, []),
        [nodes]
    );

    const value = useMemo(() => {
        const resetNodesEdges = () => {
            setNodes([]);
            setEdges([]);
        };
        return {
            nodes,
            setNodes,
            edges,
            setEdges,
            contextVariables,
            setContextVariables,
            onNodesChange,
            onEdgesChange,
            flowOrder,
            totalOrderedFlows,
            resetNodesEdges,
            incompleteNodeIds,
        };
    }, [
        nodes,
        setNodes,
        edges,
        setEdges,
        contextVariables,
        setContextVariables,
        onNodesChange,
        onEdgesChange,
        flowOrder,
        totalOrderedFlows,
        incompleteNodeIds,
    ]);

    return <NodesEdgesContext.Provider value={value}>{children}</NodesEdgesContext.Provider>;
};

export default NodesEdgesProvider;

const ALPHA_ARRAY = [
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z',
];
const orderNodes = (nodes: FlowNode[], edges: FlowEdge[]): [FlowOrderItem[], number] => {
    const { sourceIds, targetIds, flowEdges } = edges.reduce(
        (acc, edge) => {
            if (edge.sourceHandle.includes('flow-source')) {
                acc.sourceIds.add(edge.source);
                acc.targetIds.add(edge.target);
                acc.flowEdges.push(edge);
            }
            return acc;
        },
        {
            sourceIds: new Set<string>(),
            targetIds: new Set<string>(),
            flowEdges: [] as FlowEdge[],
        }
    );
    const rootNodeIds = [...sourceIds].filter((id) => !targetIds.has(id));
    const flows = rootNodeIds.reduce((acc: FlowOrderItem[], rootNodeId: string) => {
        const flow = [];
        let sectionNum = 0;
        let subsectionIndex = -1;
        let questionNum = 0;
        let currentNode = nodes.find((n) => n.id === rootNodeId);
        while (currentNode) {
            const { id, type } = currentNode;

            if (type === 'question') {
                questionNum += 1;
                const fullSectionNum = `${sectionNum}${subsectionIndex >= 0 ? ALPHA_ARRAY[subsectionIndex] : ''}`;
                flow.push({
                    id,
                    number: `${fullSectionNum}.${questionNum}`,
                });
            } else if (type !== 'decision' && currentNode.data.isSubsection) {
                if (currentNode.data.isFirstSubsection) {
                    sectionNum += 1;
                    subsectionIndex = -1;
                }
                subsectionIndex += 1;
                questionNum = 0;
                flow.push({
                    id,
                    number: `${sectionNum}${ALPHA_ARRAY[subsectionIndex]}`,
                });
            } else {
                sectionNum += 1;
                subsectionIndex = -1;
                questionNum = 0;
                flow.push({ id, number: String(sectionNum) });
            }
            const nextEdge = flowEdges.find((e) => e.source === id);
            currentNode = nextEdge ? nodes.find((n) => n.id === nextEdge.target) : undefined;
        }
        return [...acc, ...flow];
    }, []);
    return [flows, rootNodeIds.length ?? 0];
};
